import _slicedToArray from "/codebuild/output/src2483311815/src/git-codecommit.eu-west-1.amazonaws.com/v1/repos/sfx_itransfer_agent_portal/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import React__default, { useState, useCallback } from 'react';
import { _ as _objectSpread2 } from '../_rollupPluginBabelHelpers-05128ded.js';
import useSendbirdStateContext from '../useSendbirdStateContext.js';
import '../withSendbird.js';

/**
 * The default value of `checked` should be null
 * to support both case of controlled and uncontrolled component
 * ref: https://github.com/aaronshaf/react-toggle#props
 */
var noop = function noop() {/* noop */};
var TOGGLE_DEFAULT_VALUE = {
  checked: null,
  defaultChecked: false,
  disabled: false,
  onChange: noop,
  onFocus: noop,
  onBlur: noop
};
var ToggleContext = /*#__PURE__*/React__default.createContext(TOGGLE_DEFAULT_VALUE);
function useToggleContext() {
  var context = React__default.useContext(ToggleContext);
  if (context === undefined) {
    throw new Error('@sendbird/uikit-react/ui/Toggle: useToggleContext must be used within a ToggleContainer.');
  }
  return context;
}

// Props Explanation https://github.com/aaronshaf/react-toggle#props
function ToggleContainer(_ref) {
  var _ref$checked = _ref.checked,
    checked = _ref$checked === void 0 ? TOGGLE_DEFAULT_VALUE.checked : _ref$checked,
    _ref$defaultChecked = _ref.defaultChecked,
    defaultChecked = _ref$defaultChecked === void 0 ? TOGGLE_DEFAULT_VALUE.defaultChecked : _ref$defaultChecked,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? TOGGLE_DEFAULT_VALUE.disabled : _ref$disabled,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? TOGGLE_DEFAULT_VALUE.onChange : _ref$onChange,
    _ref$onFocus = _ref.onFocus,
    _onFocus = _ref$onFocus === void 0 ? TOGGLE_DEFAULT_VALUE.onFocus : _ref$onFocus,
    _ref$onBlur = _ref.onBlur,
    _onBlur = _ref$onBlur === void 0 ? TOGGLE_DEFAULT_VALUE.onBlur : _ref$onBlur,
    children = _ref.children;
  var _useState = useState(defaultChecked || false),
    _useState2 = _slicedToArray(_useState, 2),
    isChecked = _useState2[0],
    setChecked = _useState2[1];
  var handleChange = useCallback(function (e) {
    if (disabled) {
      return;
    }
    if (checked === null) {
      setChecked(e.currentTarget.checked);
    }
    onChange(e);
  }, [onChange, checked]);
  return /*#__PURE__*/React__default.createElement(ToggleContext.Provider, {
    value: {
      checked: checked !== null ? checked : isChecked,
      disabled: disabled,
      onChange: handleChange,
      onFocus: function onFocus(e) {
        if (!disabled) {
          _onFocus(e);
        }
      },
      onBlur: function onBlur(e) {
        if (!disabled) {
          _onBlur(e);
        }
      }
    }
  }, children);
}
function filterNumber(input) {
  if (typeof input !== 'string' && typeof input !== 'number') {
    try {
      var _useSendbirdStateCont = useSendbirdStateContext(),
        config = _useSendbirdStateCont.config;
      var logger = config.logger;
      logger.warning('@sendbird/uikit-react/ui/Toggle: TypeError - expected string or number.', input);
    } catch (_) {/* noop */}
    return [];
  }
  if (typeof input === 'number') {
    return [input];
  }
  var regex = /(-?\d+)(\.\d+)?/g;
  var numbers = input.match(regex) || [];
  return numbers.map(parseFloat);
}
function ToggleUI(props) {
  var _filterNumber;
  var _props$reversed = props.reversed,
    reversed = _props$reversed === void 0 ? false : _props$reversed,
    _props$width = props.width,
    width = _props$width === void 0 ? '40px' : _props$width,
    _props$animationDurat = props.animationDuration,
    animationDuration = _props$animationDurat === void 0 ? '0.5s' : _props$animationDurat,
    _props$style = props.style,
    style = _props$style === void 0 ? {} : _props$style,
    _props$name = props.name,
    name = _props$name === void 0 ? '' : _props$name,
    _props$id = props.id,
    id = _props$id === void 0 ? '' : _props$id,
    _props$ariaLabel = props.ariaLabel,
    ariaLabel = _props$ariaLabel === void 0 ? '' : _props$ariaLabel,
    _props$ariaLabelledby = props.ariaLabelledby,
    ariaLabelledby = _props$ariaLabelledby === void 0 ? '' : _props$ariaLabelledby;
  var _useToggleContext = useToggleContext(),
    checked = _useToggleContext.checked,
    disabled = _useToggleContext.disabled,
    _onChange = _useToggleContext.onChange,
    onFocus = _useToggleContext.onFocus,
    onBlur = _useToggleContext.onBlur; // animation should not be activated in the initialization step
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    animatedClassName = _useState4[0],
    setAnimatedClassName = _useState4[1];
  var toggleWidth = (_filterNumber = filterNumber(width)) === null || _filterNumber === void 0 ? void 0 : _filterNumber[0];
  var toggleHeight = toggleWidth / 2;
  // The size of dot should be 60% of toggle height
  var dotSize = toggleHeight * 0.6;
  return /*#__PURE__*/React__default.createElement("label", {
    className: ['sendbird-input-toggle-button', animatedClassName, checked ? 'sendbird-input-toggle-button--checked' : 'sendbird-input-toggle-button--unchecked', disabled ? 'sendbird-input-toggle-button--disabled' : [], reversed ? 'sendbird-input-toggle-button--reversed' : []].flat().join(' '),
    style: _objectSpread2({
      width: "".concat(toggleWidth, "px"),
      height: "".concat(toggleHeight, "px"),
      borderRadius: "".concat(dotSize, "px")
    }, style)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: ['sendbird-input-toggle-button__inner-dot', checked ? 'sendbird-input-toggle-button__inner-dot--activate' : 'sendbird-input-toggle-button__inner-dot--inactivate'].join(' '),
    style: {
      width: "".concat(dotSize, "px"),
      height: "".concat(dotSize, "px"),
      animationDuration: animationDuration
    }
  }), /*#__PURE__*/React__default.createElement("input", {
    type: "checkbox",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    name: name,
    id: id,
    checked: checked,
    disabled: disabled,
    onChange: function onChange(e) {
      _onChange(e);
      setAnimatedClassName(e.currentTarget.checked ? 'sendbird-input-toggle-button--turned-on' : 'sendbird-input-toggle-button--turned-off');
    },
    onFocus: onFocus,
    onBlur: onBlur
  }));
}
function Toggle(props) {
  var checked = props.checked,
    defaultChecked = props.defaultChecked,
    disabled = props.disabled,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    className = props.className,
    reversed = props.reversed,
    width = props.width,
    animationDuration = props.animationDuration,
    style = props.style,
    name = props.name,
    id = props.id,
    ariaLabel = props.ariaLabel,
    ariaLabelledby = props.ariaLabelledby;
  return /*#__PURE__*/React__default.createElement("div", {
    className: "sendbird-ui-toggle ".concat(className)
  }, /*#__PURE__*/React__default.createElement(ToggleContainer, {
    checked: checked,
    defaultChecked: defaultChecked,
    disabled: disabled,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur
  }, /*#__PURE__*/React__default.createElement(ToggleUI, {
    reversed: reversed,
    width: width,
    animationDuration: animationDuration,
    style: style,
    name: name,
    id: id,
    ariaLabel: ariaLabel,
    ariaLabelledby: ariaLabelledby
  })));
}
export { Toggle, ToggleContainer, ToggleUI, useToggleContext };